// muscleReadiness.js — per-region training readiness.
//
// The global readiness verdict (HARD/MEDIUM/EASY/REST) is systemic: it
// answers "how recovered is your whole body today?" But for weight
// training the more useful question is per-region: your legs can be
// fried while your upper body is fresh. This derives a readiness class
// for each region by taking the WORSE of:
//   (a) your systemic verdict, and
//   (b) how recently that region's muscles were trained (its own local
//       recovery window — ~48–72h for hypertrophy/strength work).
//
// Deterministic, no AI: it reads the same muscleRecency map the readiness
// engine already computes, so it can never contradict the global call —
// it only ever scopes it DOWN for regions that need more recovery.

export const REGIONS = ["Push", "Pull", "Legs", "Core"];

// Maps the exercise library's muscle names onto the four training
// regions. Rear delts / forearms group with Pull; lower back + obliques
// group with Core.
const MUSCLE_REGION = {
  Chest: "Push", Shoulders: "Push", Triceps: "Push",
  Back: "Pull", Lats: "Pull", Biceps: "Pull", "Rear delts": "Pull", Forearms: "Pull",
  Legs: "Legs", Quads: "Legs", Hamstrings: "Legs", Glutes: "Legs", Calves: "Legs",
  Abductors: "Legs", Adductors: "Legs",
  Core: "Core", Obliques: "Core", "Lower back": "Core",
};

export function regionForMuscle(muscle) {
  return MUSCLE_REGION[String(muscle || "").trim()] || null;
}

const RANK = { REST: 0, EASY: 1, MEDIUM: 2, HARD: 3 };
const CLASS_BY_RANK = ["REST", "EASY", "MEDIUM", "HARD"];

export function classRank(cls) {
  return RANK[String(cls || "MEDIUM").toUpperCase()] ?? 2;
}

// Local recovery cap from days since the muscle was last trained.
//   0  (today)     → REST   — freshly worked, let it recover
//   1  (yesterday) → EASY   — still rebuilding inside the 48h window
//   2              → MEDIUM — most fibres recovered, go moderate
//   ≥3 / never     → HARD   — fully recovered
export function recoveryClass(daysAgo) {
  if (daysAgo == null) return "HARD";
  if (daysAgo <= 0) return "REST";
  if (daysAgo === 1) return "EASY";
  if (daysAgo === 2) return "MEDIUM";
  return "HARD";
}

// A region is only as ready as the more conservative of the systemic
// verdict and its local recovery cap.
export function combineClass(globalClass, localClass) {
  return CLASS_BY_RANK[Math.min(classRank(globalClass), classRank(localClass))];
}

function reasonFor(region, daysAgo) {
  if (daysAgo == null) return `${region} not trained in 7d — fully fresh.`;
  if (daysAgo <= 0)    return `${region} trained today — let it recover.`;
  if (daysAgo === 1)   return `${region} trained yesterday — still rebuilding (48h window).`;
  if (daysAgo === 2)   return `${region} 2 days out — most fibres recovered, go moderate.`;
  return `${region} ${daysAgo}d rested — recovered and ready.`;
}

// muscleRecency: { Chest: 1, Legs: 3, ... } days-ago per muscle (7d window).
// Returns one entry per region with the binding (most-recently-trained)
// muscle and the combined class.
export function computeRegionReadiness(globalClass, muscleRecency = {}) {
  const perRegion = {}; // region → { daysAgo, muscle }
  for (const [muscle, daysAgo] of Object.entries(muscleRecency || {})) {
    const region = regionForMuscle(muscle);
    if (!region || daysAgo == null) continue;
    if (perRegion[region] == null || daysAgo < perRegion[region].daysAgo) {
      perRegion[region] = { daysAgo, muscle };
    }
  }
  return REGIONS.map((region) => {
    const hit = perRegion[region] || null;
    const daysAgo = hit ? hit.daysAgo : null;
    const local = recoveryClass(daysAgo);
    const cls = combineClass(globalClass, local);
    return {
      region,
      cls,
      daysAgo,
      muscle: hit?.muscle || null,
      cappedByRecovery: classRank(local) < classRank(globalClass),
      reason: reasonFor(region, daysAgo),
    };
  });
}

// Per-muscle readiness for the prescription engine — combine a single
// muscle's recency with the systemic verdict.
export function muscleReadinessClass(globalClass, daysAgo) {
  return combineClass(globalClass, recoveryClass(daysAgo));
}
